import type { DialogRenderProps } from "react-aria-components";
import React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Button2 } from "@/ui2/atoms/Button";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import CheckMarkIcon from "@/assets/images/icons2/icon--checkmark.svg?component";
import { Modal2, ModalDivider } from "@/ui2/molecules/Modal";
import { showToast } from "@/providers/ToastProvider";
import { useAcuityResolution } from "@/features/acuity/api/mutations/useAcuityResolution";
import type { UUID } from "@/core/types";

import { acuityKeys } from "../../(header-acuity)/api/keyFactory";

export function ApproveAcuityModal({ entity_id, patientId }: { entity_id: UUID; patientId: UUID }) {
  const queryClient = useQueryClient();
  const { mutateAsync: submitResolution } = useAcuityResolution({
    onError: (error) =>
      captureErrorMessage("Failed to approve acuity override", {
        tags: { error: parseError({ error }) },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: acuityKeys.history(patientId),
      });
      showToast([{ heading: "Acuity override approved", variant: "success" }]);
    },
  });

  return (
    <Modal2
      title="Confirm Acuity Override"
      trigger={
        <Button2 icon={<CheckMarkIcon />} size="sm" variant="primary">
          Approve
        </Button2>
      }
    >
      {({ close }: DialogRenderProps) => (
        <>
          <p className="body-md">
            This change will take effect immediately and be recorded in the patient&apos;s clinical
            history.
          </p>

          <ModalDivider />

          <div className="tw:flex tw:justify-end tw:gap-3">
            <Button2 onPress={close} variant="secondary">
              Cancel
            </Button2>

            <Button2
              icon={<CheckMarkIcon />}
              onPress={() => {
                submitResolution({ body: { entity_id, status: "APPROVED" }, patientId });

                close();
              }}
              variant="primary"
            >
              Approve Override
            </Button2>
          </div>
        </>
      )}
    </Modal2>
  );
}
